import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { Search, RefreshCw, X } from 'lucide-react';
import '../styles/LevelChart.css';

const TIMEFRAMES = [
  { label: '1H', value: '1h' },
  { label: '4H', value: '4h' },
  { label: '1D', value: '1d' },
  { label: '1W', value: '1w' },
  { label: '1M', value: '1M' },
];

function LevelChart({ levels, subLevels, title = 'Chart', chartId = 'main' }) {
  const CHART_STORAGE_KEY = `level_chart_state_${chartId}`;
  
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  
  const [symbol, setSymbol] = useState(() => {
    const saved = localStorage.getItem(CHART_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.symbol || 'BTCUSDT';
    }
    return 'BTCUSDT';
  });
  
  const [timeframe, setTimeframe] = useState(() => {
    const saved = localStorage.getItem(CHART_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.timeframe || '4h';
    }
    return '4h';
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [allSymbols, setAllSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  const searchRef = useRef(null);
  const wsRef = useRef(null);
  const lastCandleRef = useRef(null);

  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem(CHART_STORAGE_KEY, JSON.stringify({ symbol, timeframe }));
  }, [symbol, timeframe, CHART_STORAGE_KEY]);

  // Fetch all Binance futures symbols on mount
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        const response = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
        const data = await response.json();
        const symbols = data.symbols
          .filter(s => s.status === 'TRADING' && s.contractType === 'PERPETUAL')
          .map(s => ({
            symbol: s.symbol,
            baseAsset: s.baseAsset,
            quoteAsset: s.quoteAsset,
          }))
          .sort((a, b) => a.symbol.localeCompare(b.symbol));
        setAllSymbols(symbols);
      } catch (err) {
        console.error('Failed to fetch symbols:', err);
        setError('Failed to load symbols. Only Binance Perpetual Futures supported.');
      }
    };
    fetchSymbols();
  }, []);

  // Filter symbols based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const query = searchQuery.toUpperCase();
    const filtered = allSymbols
      .filter(s => 
        s.symbol.includes(query) || 
        s.baseAsset.includes(query)
      )
      .slice(0, 20);
    setSearchResults(filtered);
  }, [searchQuery, allSymbols]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: 'solid', color: '#0d1117' },
        textColor: '#8b949e',
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      crosshair: {
        mode: 0,
        vertLine: { color: '#58a6ff', width: 1, style: 2, labelBackgroundColor: '#58a6ff' },
        horzLine: { color: '#58a6ff', width: 1, style: 2, labelBackgroundColor: '#58a6ff' },
      },
      rightPriceScale: {
        borderColor: '#21262d',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: '#21262d',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScale: { axisPressedMouseMove: true },
      handleScroll: { vertTouchDrag: true },
    });

    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#00c853',
      downColor: '#ff1744',
      borderUpColor: '#00c853',
      borderDownColor: '#ff1744',
      wickUpColor: '#00c853',
      wickDownColor: '#ff1744',
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ 
          width: chartContainerRef.current.clientWidth,
          height: 400,
        });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, []);

  // Fetch klines data
  const fetchKlines = useCallback(async () => {
    if (!symbol || !candlestickSeriesRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${timeframe}&limit=500`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch data. Only Binance Perpetual Futures supported.');
      }
      
      const data = await response.json();
      
      if (data.code) {
        throw new Error(`Binance Error: ${data.msg}. Only Perpetual Futures contracts supported.`);
      }
      
      const candles = data.map(d => ({
        time: d[0] / 1000,
        open: parseFloat(d[1]),
        high: parseFloat(d[2]),
        low: parseFloat(d[3]),
        close: parseFloat(d[4]),
      }));

      candlestickSeriesRef.current.setData(candles);
      
      // Set current price from last candle
      if (candles.length > 0) {
        setCurrentPrice(candles[candles.length - 1].close);
      }

      // Fit content
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
      }
    } catch (err) {
      setError(err.message || 'Only Binance Perpetual Futures supported.');
      console.error('Klines fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe]);

  // Fetch data when symbol or timeframe changes
  useEffect(() => {
    fetchKlines();
  }, [fetchKlines]);

  // WebSocket for real-time candle updates
  useEffect(() => {
    if (!symbol || !candlestickSeriesRef.current) return;

    let isCleaningUp = false;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const wsSymbol = symbol.toLowerCase();
    const wsUrl = `wss://fstream.binance.com/ws/${wsSymbol}@kline_${timeframe}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!isCleaningUp) {
        setIsConnected(true);
      }
    };

    ws.onmessage = (event) => {
      if (isCleaningUp) return;
      try {
        const data = JSON.parse(event.data);
        if (data.k) {
          const kline = data.k;
          const candle = {
            time: Math.floor(kline.t / 1000),
            open: parseFloat(kline.o),
            high: parseFloat(kline.h),
            low: parseFloat(kline.l),
            close: parseFloat(kline.c),
          };

          // Update chart with new candle data
          if (candlestickSeriesRef.current) {
            candlestickSeriesRef.current.update(candle);
            setCurrentPrice(candle.close);
            lastCandleRef.current = candle;
          }
        }
      } catch (err) {
        console.error('WebSocket parse error:', err);
      }
    };

    ws.onerror = () => {
      // Only log errors if not cleaning up (intentional close)
      if (!isCleaningUp) {
        setIsConnected(false);
      }
    };

    ws.onclose = () => {
      if (!isCleaningUp) {
        setIsConnected(false);
      }
    };

    return () => {
      isCleaningUp = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [symbol, timeframe]);

  // Update price lines when levels change
  useEffect(() => {
    if (!candlestickSeriesRef.current) return;

    // Remove existing price lines
    priceLinesRef.current.forEach(line => {
      try {
        candlestickSeriesRef.current.removePriceLine(line);
      } catch (e) {
        // Line might already be removed
      }
    });
    priceLinesRef.current = [];

    if (!levels) return;

    // Add Swing levels (H26) - solid lines
    if (levels.swing) {
      Object.entries(levels.swing).forEach(([pct, level]) => {
        // Resistance (UP) - green
        const upLine = candlestickSeriesRef.current.createPriceLine({
          price: level.up,
          color: '#00c853',
          lineWidth: pct === '100' ? 2 : 1,
          lineStyle: 0, // Solid
          axisLabelVisible: true,
          title: `${pct}% R`,
        });
        priceLinesRef.current.push(upLine);

        // Support (DOWN) - red
        const downLine = candlestickSeriesRef.current.createPriceLine({
          price: level.down,
          color: '#ff1744',
          lineWidth: pct === '100' ? 2 : 1,
          lineStyle: 0, // Solid
          axisLabelVisible: true,
          title: `${pct}% S`,
        });
        priceLinesRef.current.push(downLine);
      });
    }

    // Add Extended levels (F4) - dashed lines
    if (levels.extended) {
      Object.entries(levels.extended).forEach(([pct, level]) => {
        // Resistance (UP) - cyan dashed
        const upLine = candlestickSeriesRef.current.createPriceLine({
          price: level.up,
          color: '#00bcd4',
          lineWidth: pct === '100' ? 2 : 1,
          lineStyle: 2, // Dashed
          axisLabelVisible: true,
          title: `E${pct}% R`,
        });
        priceLinesRef.current.push(upLine);

        // Support (DOWN) - orange dashed
        const downLine = candlestickSeriesRef.current.createPriceLine({
          price: level.down,
          color: '#ff9800',
          lineWidth: pct === '100' ? 2 : 1,
          lineStyle: 2, // Dashed
          axisLabelVisible: true,
          title: `E${pct}% S`,
        });
        priceLinesRef.current.push(downLine);
      });
    }

    // Add Sub-levels (dimmer, dotted lines)
    if (subLevels) {
      // Swing sub-levels
      if (subLevels.swing) {
        Object.entries(subLevels.swing).forEach(([pct, level]) => {
          const upLine = candlestickSeriesRef.current.createPriceLine({
            price: level.up,
            color: 'rgba(0, 200, 83, 0.5)',
            lineWidth: 1,
            lineStyle: 1, // Dotted
            axisLabelVisible: false,
            title: '',
          });
          priceLinesRef.current.push(upLine);

          const downLine = candlestickSeriesRef.current.createPriceLine({
            price: level.down,
            color: 'rgba(255, 23, 68, 0.5)',
            lineWidth: 1,
            lineStyle: 1, // Dotted
            axisLabelVisible: false,
            title: '',
          });
          priceLinesRef.current.push(downLine);
        });
      }

      // Extended sub-levels
      if (subLevels.extended) {
        Object.entries(subLevels.extended).forEach(([pct, level]) => {
          const upLine = candlestickSeriesRef.current.createPriceLine({
            price: level.up,
            color: 'rgba(0, 188, 212, 0.5)',
            lineWidth: 1,
            lineStyle: 1, // Dotted
            axisLabelVisible: false,
            title: '',
          });
          priceLinesRef.current.push(upLine);

          const downLine = candlestickSeriesRef.current.createPriceLine({
            price: level.down,
            color: 'rgba(255, 152, 0, 0.5)',
            lineWidth: 1,
            lineStyle: 1, // Dotted
            axisLabelVisible: false,
            title: '',
          });
          priceLinesRef.current.push(downLine);
        });
      }
    }

  }, [levels, subLevels]);

  const handleSymbolSelect = (sym) => {
    setSymbol(sym.symbol);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
    if (!searchQuery && allSymbols.length > 0) {
      // Show popular symbols when empty
      setSearchResults(allSymbols.filter(s => 
        ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'DOGEUSDT', 'ADAUSDT', 'AVAXUSDT'].includes(s.symbol)
      ));
    }
  };

  return (
    <div className="level-chart">
      <div className="chart-header">
        <h4>{title}</h4>
        <span className="chart-source">
          Binance Perpetual Futures Only
          {isConnected && <span className="live-indicator"> - LIVE</span>}
        </span>
      </div>
      
      <div className="chart-controls">
        <div className="symbol-search" ref={searchRef}>
          <div className="search-input-wrapper">
            <Search size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={handleSearchFocus}
              placeholder={symbol}
              className="symbol-input"
            />
            {searchQuery && (
              <X 
                size={14} 
                className="clear-search" 
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
              />
            )}
          </div>
          
          {showDropdown && searchResults.length > 0 && (
            <div className="symbol-dropdown">
              {searchResults.map((sym) => (
                <div 
                  key={sym.symbol}
                  className={`symbol-option ${sym.symbol === symbol ? 'active' : ''}`}
                  onClick={() => handleSymbolSelect(sym)}
                >
                  <span className="symbol-name">{sym.baseAsset}</span>
                  <span className="symbol-pair">{sym.symbol}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="timeframe-selector">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              className={`tf-btn ${timeframe === tf.value ? 'active' : ''}`}
              onClick={() => setTimeframe(tf.value)}
            >
              {tf.label}
            </button>
          ))}
        </div>

        <button 
          className="refresh-btn" 
          onClick={fetchKlines}
          disabled={loading}
          title="Refresh chart"
        >
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {error && (
        <div className="chart-error">
          <span>{error}</span>
        </div>
      )}

      <div className="chart-container" ref={chartContainerRef}>
        {loading && (
          <div className="chart-loading">
            <RefreshCw size={24} className="spinning" />
            <span>Loading chart...</span>
          </div>
        )}
      </div>

      <div className="chart-info">
        <span className="current-symbol">{symbol}</span>
        {currentPrice && (
          <span className="current-price">${currentPrice.toFixed(2)}</span>
        )}
        <span className="chart-attribution">
          Chart by <a href="https://www.tradingview.com" target="_blank" rel="noopener noreferrer">TradingView</a>
        </span>
      </div>
    </div>
  );
}

export default LevelChart;
